// Путь: D:\QR\QR-WEB\dashboard\src\components\Admin\AdminSubscriptions\AdminSubscriptions.jsx
// Название: Admin Subscriptions Management
// Версия: 1.0.1
// Дата: 29.01.2026

import React, { useState, useEffect } from 'react';
import { api } from '../../../services/api';
import './AdminSubscriptions.css';

const AdminSubscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, trial: 0, expired: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Загрузка подписок
      const subsRes = await api.get('/admin/subscriptions');
      if (subsRes.success) {
        setSubscriptions(subsRes.subscriptions || []);
        setStats(subsRes.stats || { total: 0, active: 0, trial: 0, expired: 0 });
      }

      // Загрузка планов
      const plansRes = await api.get('/subscriptions/plans');
      if (plansRes.success) {
        setPlans(plansRes.plans || []);
      }
    } catch (error) {
      console.error('Load error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { label: 'Активна', class: 'badge-success' },
      trial: { label: 'Триал', class: 'badge-info' },
      expired: { label: 'Истекла', class: 'badge-danger' },
      cancelled: { label: 'Отменена', class: 'badge-warning' }
    };
    const badge = badges[status] || { label: status, class: 'badge-default' };
    return <span className={`admin-badge ${badge.class}`}>{badge.label}</span>;
  };

  const getPlanBadge = (planCode) => {
    const badges = {
      trial: { label: 'Триал', class: 'plan-trial' },
      monthly: { label: 'Месячный', class: 'plan-monthly' },
      yearly: { label: 'Годовой', class: 'plan-yearly' }
    };
    const badge = badges[planCode] || { label: planCode, class: 'plan-default' };
    return <span className={`plan-badge ${badge.class}`}>{badge.label}</span>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getDaysLeft = (expiresAt) => {
    if (!expiresAt) return null;
    const days = Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24));
    if (days < 0) return <span className="text-danger">Истекла</span>;
    if (days <= 3) return <span className="text-warning">{days} дн.</span>;
    return <span className="text-success">{days} дн.</span>;
  };

  const filteredSubscriptions = subscriptions.filter(sub => {
    if (filter === 'all') return true;
    if (filter === 'trial') return sub.plan_code === 'trial';
    if (filter === 'paid') return sub.plan_code !== 'trial';
    if (filter === 'expired') return sub.status === 'expired';
    return true;
  });

  if (loading) {
    return (
      <div className="admin-subscriptions">
        <div className="admin-loading">⏳ Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="admin-subscriptions">
      <div className="admin-page-header">
        <h1>💎 Подписки</h1>
        <p>Управление подписками бизнесов</p>
      </div>

      {/* Stats Cards */}
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-info">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Всего</span>
          </div>
        </div>
        <div className="admin-stat-card success">
          <div className="stat-icon">✅</div>
          <div className="stat-info">
            <span className="stat-value">{stats.active}</span>
            <span className="stat-label">Активных</span>
          </div>
        </div>
        <div className="admin-stat-card info">
          <div className="stat-icon">🎁</div>
          <div className="stat-info">
            <span className="stat-value">{stats.trial}</span>
            <span className="stat-label">Триал</span>
          </div>
        </div>
        <div className="admin-stat-card danger">
          <div className="stat-icon">⏰</div>
          <div className="stat-info">
            <span className="stat-value">{stats.expired}</span>
            <span className="stat-label">Истекших</span>
          </div>
        </div>
      </div>

      {/* Plans Overview */}
      <div className="admin-section">
        <h2>📋 Тарифные планы</h2>
        <div className="plans-grid">
          {plans.map(plan => (
            <div key={plan.id} className={`plan-card plan-${plan.code}`}>
              <div className="plan-name">{plan.name}</div>
              <div className="plan-price">
                {plan.price_eur > 0 ? `€${plan.price_eur}` : 'Бесплатно'}
              </div>
              <div className="plan-duration">{plan.duration_days} дней</div>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="admin-filters">
        <button 
          className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          Все ({subscriptions.length})
        </button>
        <button 
          className={`filter-btn ${filter === 'trial' ? 'active' : ''}`}
          onClick={() => setFilter('trial')}
        >
          Триал
        </button>
        <button 
          className={`filter-btn ${filter === 'paid' ? 'active' : ''}`}
          onClick={() => setFilter('paid')}
        >
          Платные
        </button>
        <button 
          className={`filter-btn ${filter === 'expired' ? 'active' : ''}`}
          onClick={() => setFilter('expired')}
        >
          Истекшие
        </button>
      </div>

      {/* Subscriptions Table */}
      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Бизнес</th>
              <th>План</th>
              <th>Статус</th>
              <th>Начало</th>
              <th>Окончание</th>
              <th>Осталось</th>
              <th>Оплачено</th>
            </tr>
          </thead>
          <tbody>
            {filteredSubscriptions.length === 0 ? (
              <tr>
                <td colSpan="8" className="empty-row">Нет подписок</td>
              </tr>
            ) : (
              filteredSubscriptions.map(sub => (
                <tr key={sub.id}>
                  <td>#{sub.id}</td>
                  <td>
                    <div className="business-cell">
                      <span className="business-email">{sub.business_email || `ID: ${sub.business_id}`}</span>
                    </div>
                  </td>
                  <td>{getPlanBadge(sub.plan_code)}</td>
                  <td>{getStatusBadge(sub.status)}</td>
                  <td>{formatDate(sub.starts_at)}</td>
                  <td>{formatDate(sub.expires_at)}</td>
                  <td>{getDaysLeft(sub.expires_at)}</td>
                  <td>
                    {sub.price_paid > 0 ? (
                      <span className="text-success">€{sub.price_paid}</span>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminSubscriptions;